/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { IS_REGION_US, NumberTypes, State } from '@gh/common-shared';
import { ID } from '@gh/common-shared-data';
import { FocusedCell, GridLayout } from '@gh/core-ui-grid';
import { distinctUntilChanged$, filter$, map$, switchMap$ } from '@gh/rx/operators';
import { CellDoubleClickedEvent } from 'ag-grid-community';
import { Observable, Subscription, combineLatest } from 'rxjs';

import {
  getJeCostCenterId,
  getJeItemId,
  isJeFreeItem,
  isJeFreeOrComponentItem,
  JeItem,
  JeItemType,
} from '../../../resources/job-estimate-details.model';
import { JeCommandService } from '../je-command.service';
import {
  ensureJeSalesTaxDivider,
  JeDetailsState,
  JeFocusedTable,
  queryJeCostCenterItems,
  queryJeEditable,
  queryJeFocusedCell,
  queryJeItemLayout,
  queryJeSalesTaxRates,
  queryJeSelectedCostCenter,
  queryJeSelectedCostCenterId,
  queryJeSelectedItemId,
} from '../je-details.state';
import { JeDetailsCcItemGridDefinition } from './je-details-cc-item-grid-definition.service';
import { reduce } from 'lodash';
import { combineLatest$ } from '@gh/rx/index';

@Component({
  selector: 'gh-je-details-cc-item-list',
  template: require('./je-details-cc-item-list.component.html'),
  styles: [require('./je-details-cc-item-list.component.scss')],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [JeDetailsCcItemGridDefinition],
})
export class JeDetailsCcItemListComponent implements OnInit, OnDestroy {
  total$: Observable<number>;
  items$: Observable<JeItem[]>;
  selectedItemId$: Observable<Maybe<ID>>;
  focusedCell$: Observable<Maybe<FocusedCell>>;
  str$: Observable<string>;
  editable$: Observable<boolean>;
  ccCost$: Observable<number>;
  combineCosts$: Observable<boolean>;

  isUsRegion = IS_REGION_US;

  private costCenterId: ID;
  private costCenterIdSubscription: Subscription;

  constructor(private route: ActivatedRoute,
              public gridDefinition: JeDetailsCcItemGridDefinition,
              private state: State<JeDetailsState>,
              private commands: JeCommandService) {

  }

  ngOnInit(): void {
    this.gridDefinition.bind(this.state.project(queryJeItemLayout));

    const { state } = this;
    const costCenterId$ = state.select(queryJeSelectedCostCenterId).pipe(filter$(Boolean));
    const costCenter$ = state.select(queryJeSelectedCostCenter).pipe(filter$(Boolean));

    this.items$ = costCenter$.pipe(switchMap$((cc: any) => state.select(queryJeCostCenterItems, getJeCostCenterId(cc)).pipe(
      map$(ensureJeSalesTaxDivider))));
    this.ccCost$ = costCenter$.pipe(map$((component: any) => component.cost || 0), distinctUntilChanged$());
    this.total$ = this.items$.pipe(
      map$((items) => reduce(items, (sum, n) =>  sum + n.extension, 0) || 0), distinctUntilChanged$());
    this.combineCosts$ = combineLatest(this.ccCost$, this.total$).pipe(
      // tslint:disable-next-line:no-magic-numbers
      map$(([cost, total]) => cost ===  +total.toFixed(2)));
    this.selectedItemId$ = state.select(queryJeSelectedItemId);
    this.focusedCell$ = this.state.select(queryJeFocusedCell, JeFocusedTable.ItemList);
    this.str$ = this.state.select(queryJeSalesTaxRates).pipe(
      map$((rates) => rates.map((rate) => NumberTypes.Rate.format(rate.ratePct) + '%').join('; ')));

    this.costCenterIdSubscription = costCenterId$.subscribe((id: any) => this.costCenterId = id);
    this.editable$ = this.state.select(queryJeEditable);
  }

  ngOnDestroy(): void {
    this.costCenterIdSubscription.unsubscribe();
  }

  onAdd(): void {
    this.commands.costCenterItemAdd(this.costCenterId);
  }

  onCloneItems(items: JeItem): void {
    this.commands.costCenterItemClone(items);
  }
  onDelete(item: JeItem): void {
    this.commands.costCenterItemRemove(item);
  }

  canBeDeleted(item: JeItem): boolean {
    return item.type === JeItemType.Item;
  }

  canChangeCc(item: JeItem): boolean {
    return isJeFreeItem(item);
  }

  onSelectedItemChanged(item?: JeItem): void {
    if (item) {
      if (item.type === JeItemType.Item || item.type === JeItemType.Component) {
        this.commands.itemSelect(getJeItemId(item));
      }
    } else {
      this.commands.itemSelect(void 0);
    }
  }

  onFocusedCellChanged(): void {
    this.commands.resetFocusedCell();
  }

  onCellDoubleClicked({ colDef, data }: CellDoubleClickedEvent): void {
    if (this.state.project(queryJeEditable) && colDef.colId === 'variation') {
      this.commands.itemConfirmVariation(getJeItemId(data));
    }
  }

  onChangeCc(item: JeItem): void {
    this.commands.itemChangeCc(getJeItemId(item));
  }

  onOpenStr(): void {
    this.commands.openStr();
  }

  onLayoutChanged(layout: GridLayout): void {
    this.commands.layoutUpdate(layout);
  }

  filterCostCenterItem(item: JeItem): boolean {
    return isJeFreeOrComponentItem(item);
  }
}
